import React, { useCallback, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import { freeze } from '@reduxjs/toolkit';
import { useQuery } from '@apollo/client';
import KubitModal from '../KubitModal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { closeModal, ICohortModalData } from '../../redux/slices/modal';
import {
  ISearchItemFieldsFragment,
  ISearchQuery,
  ISearchQueryVariables,
  Maybe,
  QueryType,
  SearchEntityType,
} from '../../utils/apollo/graphqlSchema';
import { useDebounce } from '../../utils/hooks';
import {
  ISearchInput,
  SearchableEntityTypes,
} from '../../Layout/Header/UniversalSearch/types';
import AdvancedSearchMenu from '../../Layout/Header/UniversalSearch/AdvancedSearchMenu';
import { getAdvancedSearchConfig } from '../../Layout/Header/UniversalSearch/SearchResultItem/utils';
import SearchResultItem from '../../Layout/Header/UniversalSearch/SearchResultItem';
import { SEARCH_QUERY } from '../../utils/apollo/universalSearch';
import { assertCondition } from '../../utils/types';
import { selectModalData } from '../../redux/selectors/modal';
import { Loader } from '../Loaders';
import { useNavigate } from 'react-router-dom';
import { getCohortPagePath } from '../../utils';
import SearchInput from '../Form/SearchInput';
import * as SearchMenuUI from '../../Layout/Header/UniversalSearch/SearchMenuUI';

interface IProps {
  type: 'add-cohort' | 'open-cohort';
}

const SEARCH_COUNT = 10;

const defaultSearchInput = freeze<ISearchInput>(
  {
    term: '',
    types: [SearchEntityType.Cohort] as SearchableEntityTypes[],
    schemaIds: [],
    categoryIds: [],
    editorIds: [],
    queryTypes: [],
    isDisabled: false,
    failedReports: false,
    lastUpdateDateRange: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    pagination: {
      first: SEARCH_COUNT,
      after: 0,
    },
    favoriteOnly: false,
    activeSchemas: true,
  },
  true,
);

const CohortSearchModal = ({ type }: IProps) => {
  const [searchInput, setSearchInput] =
    useImmer<ISearchInput>(defaultSearchInput);
  const [searchResult, setSearchResult] =
    useState<Maybe<ISearchQuery['search']>>(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const modalData = useAppSelector(selectModalData) as ICohortModalData;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const onFetchSearchResult = (data: ISearchQuery) => {
    if (data.search.pageInfo.first > 0 && searchResult) {
      setSearchResult({
        ...data.search,
        entities: [...searchResult.entities, ...data.search.entities],
        pageInfo: data.search.pageInfo,
      });
    } else {
      setSearchResult(data.search);
    }
  };

  const searchInputUpdateHandler = useCallback(
    (searchInput: ISearchInput, isLoadMore: boolean) => {
      if (isLoadMore) {
        setSearchInput((draft) => {
          draft.pagination = {
            after: searchResult ? searchResult.pageInfo.last + 1 : 0,
            first: SEARCH_COUNT,
          };
        });
      } else {
        setSearchInput(() => ({
          ...searchInput,
          pagination: { first: SEARCH_COUNT, after: 0 },
        }));
      }
    },
    [setSearchInput, searchResult],
  );

  const debouncedSearchInput = useDebounce(searchInput, 300);

  const getUpdatedVariables = useMemo(() => {
    return (currSearchInput: ISearchInput) => {
      const advancedSearchConfig = getAdvancedSearchConfig([
        SearchEntityType.Cohort,
      ]);
      return {
        ...currSearchInput,
        editorIds: advancedSearchConfig.editorIds
          ? currSearchInput.editorIds
          : [],
        schemaIds: advancedSearchConfig.schemaIds
          ? currSearchInput.schemaIds
          : [],
        queryTypes: [],
        categoryIds: advancedSearchConfig.categoryIds
          ? currSearchInput.categoryIds
          : [],
      };
    };
  }, []);

  const { loading } = useQuery<ISearchQuery, ISearchQueryVariables>(
    SEARCH_QUERY,
    {
      variables: getUpdatedVariables(debouncedSearchInput),
      onCompleted: onFetchSearchResult,
    },
  );

  const handleEntityClick = async (searchItem: ISearchItemFieldsFragment) => {
    setIsLoading(true);
    try {
      assertCondition(searchItem.entity.__typename === 'IdSearchEntity');
      const { id } = searchItem.entity;

      if (type === 'add-cohort') {
        if (modalData?.onSubmit) {
          await modalData.onSubmit(id, modalData.split);
        }
      } else {
        navigate(getCohortPagePath(id));
      }

      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    searchInputUpdateHandler(searchInput, true);
  };

  const handleResetClick = () => {
    searchInputUpdateHandler(defaultSearchInput, false);
  };

  return (
    <KubitModal
      open={true}
      onClose={handleClose}
      maxWidth="max-content"
      noBodyWrapper={true}
    >
      <SearchMenuUI.Container>
        <SearchMenuUI.ContentWrapper>
          <SearchMenuUI.StaticWrapper $hasBorder={true}>
            <SearchInput
              value={searchInput.term}
              onChange={(term) =>
                searchInputUpdateHandler({ ...searchInput, term }, false)
              }
              placeholder={'Enter keywords to find cohorts'}
              autoFocus={true}
              isFullWidth={true}
            />
          </SearchMenuUI.StaticWrapper>
          {loading ? (
            <Loader />
          ) : !searchResult || searchResult?.entities?.length === 0 ? (
            <SearchMenuUI.EmptyResult>No result found</SearchMenuUI.EmptyResult>
          ) : (
            <SearchMenuUI.StyledContent>
              {searchResult?.entities?.map((result, index) => (
                <SearchResultItem
                  key={'cohort-result-' + index}
                  searchItem={result}
                  onClick={() => handleEntityClick(result)}
                  showAddButton={type === 'add-cohort'}
                  isLoading={isLoading}
                />
              ))}
              {searchResult?.pageInfo.hasMore && (
                <SearchMenuUI.LoadMoreButton
                  size="default"
                  onClick={() => loadMore()}
                >
                  Load More
                </SearchMenuUI.LoadMoreButton>
              )}
            </SearchMenuUI.StyledContent>
          )}
        </SearchMenuUI.ContentWrapper>
        <AdvancedSearchMenu
          searchInput={searchInput}
          setSearchInput={searchInputUpdateHandler}
          onReset={handleResetClick}
          supportedQueryTypes={[QueryType.CohortSample]}
        />
      </SearchMenuUI.Container>
    </KubitModal>
  );
};

export default CohortSearchModal;
